import type { Vec2 } from "@townsy/contracts";
import { forward, normalizeMove } from "@townsy/world";

/**
 * Pure keyboard/joystick -> movement-intent mapping (docs/03 section 3 "Controls").
 * No DOM here; `inputController.ts` wires events to these functions.
 */
export interface KeyState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
}

export const EMPTY_KEYS: Readonly<KeyState> = Object.freeze({ forward: false, back: false, left: false, right: false });

const CODE_TO_KEY: Record<string, keyof KeyState> = {
  KeyW: "forward",
  ArrowUp: "forward",
  KeyS: "back",
  ArrowDown: "back",
  KeyA: "left",
  ArrowLeft: "left",
  KeyD: "right",
  ArrowRight: "right",
};

export function keyForCode(code: string): keyof KeyState | null {
  return CODE_TO_KEY[code] ?? null;
}

/** Returns the same object when nothing changed so callers can compare by identity. */
export function applyKey(keys: KeyState, code: string, pressed: boolean): KeyState {
  const key = keyForCode(code);
  if (!key || keys[key] === pressed) return keys;
  return { ...keys, [key]: pressed };
}

/** Screen-space intent: x = strafe right, z = forward (toward where the camera looks). */
export function rawVector(keys: KeyState): Vec2 {
  return { x: (keys.right ? 1 : 0) - (keys.left ? 1 : 0), z: (keys.forward ? 1 : 0) - (keys.back ? 1 : 0) };
}

/**
 * Rotate a screen-space intent into world space using the camera yaw (world convention of
 * `@townsy/world`: yaw 0 faces +z, +PI/2 faces +x) and clamp it to unit length so diagonal
 * input never exceeds walking speed and never fails the `InputMove` bounds (|axis| <= 1).
 */
export function cameraRelativeMove(raw: Vec2, cameraYaw: number): Vec2 {
  if (raw.x === 0 && raw.z === 0) return { x: 0, z: 0 };
  const f = forward(Number.isFinite(cameraYaw) ? cameraYaw : 0);
  const r = { x: f.z, z: -f.x }; // right-hand perpendicular (yaw + PI/2)
  return normalizeMove(f.x * raw.z + r.x * raw.x, f.z * raw.z + r.z * raw.x);
}

/** Combine keyboard and joystick intents (either may be zero) into one unit-clamped vector. */
export function combineIntents(a: Vec2, b: Vec2): Vec2 {
  return normalizeMove(a.x + b.x, a.z + b.z);
}

export function isMoving(v: Vec2): boolean {
  return Math.abs(v.x) > 1e-6 || Math.abs(v.z) > 1e-6;
}

/** Virtual joystick geometry: touch offset from the base centre -> intent, with a dead zone. */
export function joystickVector(dx: number, dy: number, radiusPx: number, deadZonePx = 8): Vec2 {
  const len = Math.hypot(dx, dy);
  if (len < deadZonePx) return { x: 0, z: 0 };
  const scale = Math.min(1, len / radiusPx) / len;
  // Screen up (negative dy) is "forward".
  return { x: dx * scale, z: -dy * scale };
}

/**
 * Driving intent (ADR-0006): W/ArrowUp = gas (+1), S/ArrowDown = brake, then reverse once stopped
 * (-1), A/ArrowLeft = steer left (-1), D/ArrowRight = steer right (+1). The touch joystick adds its
 * vertical axis (up = gas) to the throttle and its horizontal axis to the steering. Unlike walking
 * this is car-relative, never camera-relative. Each axis is clamped to [-1, 1] (InputDrive bounds).
 */
export interface DriveIntent {
  throttle: number;
  steer: number;
}

export const NO_DRIVE: Readonly<DriveIntent> = Object.freeze({ throttle: 0, steer: 0 });

const clampAxis = (v: number): number => (!Number.isFinite(v) ? 0 : v < -1 ? -1 : v > 1 ? 1 : v);

export function driveIntent(keys: KeyState, joystick: Vec2 = { x: 0, z: 0 }): DriveIntent {
  const raw = rawVector(keys);
  return { throttle: clampAxis(raw.z + joystick.z), steer: clampAxis(raw.x + joystick.x) };
}

/**
 * E: gets into the car at whose door you stand or out of the car you drive; sits / lies down on the seat offered, or
 * stands up again (ADR-0018). No modifiers, no auto-repeat (Shift is allowed: it only means "run").
 */
export function isInteractKey(ev: { code: string; repeat?: boolean; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): boolean {
  return ev.code === "KeyE" && !ev.repeat && !ev.ctrlKey && !ev.metaKey && !ev.altKey;
}

/** Holding Shift (either side) runs instead of walking (ADR-0018). */
export function isRunKeyCode(code: string): boolean {
  return code === "ShiftLeft" || code === "ShiftRight";
}
